import { PlusIcon, TrashIcon } from '@f8n/icons';
import { styled } from '@f8n-frontend/stitches';
import React, { useEffect, useRef } from 'react';
import { match } from 'ts-pattern';

import { ControlButton } from 'components/ContentBlock';
import { ContentBlockItem } from 'components/ContentBlockItem';
import {
  ContentBlockItemNft,
  useContentItemNftContext,
} from 'components/ContentBlockItemNft';
import DropGridItem from 'components/DropGridItem';
import EditionGridItem from 'components/EditionGridItem';
import FrameGrid from 'components/FrameGrid';
import FrameGridItemDetails from 'components/FrameGridItemDetails';
import NftGridItem from 'components/NftGridItem';
import Button from 'components/base/Button';
import StoryNftModal from 'components/modals/StoryNftModal';
import { EditorMachineContext } from 'machines/editor';

import { ApiStoryGroupSize } from 'gql/api/types-api.generated';
import useModal from 'hooks/use-modal';
import { mapDropSaleConfigurationToDropSale } from 'schemas/parse/drop-sale';
import { mapApiMediaToPreviewMediaAsset } from 'utils/media-preview';
import { MomentInventoryItem } from 'utils/story/story';

import {
  LayoutChangeFn,
  StoryInventoryItem,
  StoryInventoryItemBlockProps,
} from 'types/Story';

import { StoryContentBlock } from './StoryContentBlock';
import { ContentLayoutToolbar, StoryToolbarPositioner } from './StoryToolbar';

const MAX_COLUMNS = 2;

const mapStoryInventoryItemToBlockProps = (
  storyInventoryItem: StoryInventoryItem
): StoryInventoryItemBlockProps => {
  return match<StoryInventoryItem, StoryInventoryItemBlockProps>(
    storyInventoryItem
  )
    .with(
      {
        content: {
          state: 'POPULATED',
        },
      },
      (item) => {
        return {
          id: item.id,
          state: 'POPULATED',
          inventoryItem: item.content.inventoryItem,
        };
      }
    )
    .with(
      {
        content: {
          state: 'EMPTY',
        },
      },
      (item) => {
        return {
          id: item.id,
          state: 'EMPTY',
        };
      }
    )
    .exhaustive();
};

const mapBlockPropsToStoryInventoryItem = (
  props: StoryInventoryItemBlockProps
): StoryInventoryItem => {
  return match<StoryInventoryItemBlockProps, StoryInventoryItem>(props)
    .with({ state: 'EMPTY' }, (item) => {
      return {
        type: 'INVENTORY_ITEM',
        id: item.id,
        placeholder: null,
        content: {
          state: 'EMPTY',
        },
      };
    })
    .with({ state: 'POPULATED' }, (item) => {
      return {
        type: 'INVENTORY_ITEM',
        id: item.id,
        placeholder: null,
        content: {
          state: 'POPULATED',
          inventoryItem: item.inventoryItem,
        },
      };
    })
    .exhaustive();
};

type StoryInventoryItemGroupProps = {
  items: StoryInventoryItem[];
  groupId: string;
  onUpdateItem(block: StoryInventoryItem): void;
  onRemoveItem(id: string): void;
  onAddItem(): void;
  onRemoveGroup(): void;
  onLayoutChange: LayoutChangeFn;
  layout: ApiStoryGroupSize;
  momentId: number;
  itemIdsInStory: string[];
  hasItemsToAdd: boolean;
};

export function StoryInventoryItemGroup(props: StoryInventoryItemGroupProps) {
  const focusedGroupId = EditorMachineContext.useSelector(
    (state) => state.context.focusedGroupId
  );

  const momentItemsById = EditorMachineContext.useSelector((state) => {
    return state.context.momentItems.reduce(
      (acc, item) => {
        return {
          ...acc,
          [item.id]: item,
        };
      },
      {} as Record<string, MomentInventoryItem>
    );
  });

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusedGroupId === props.groupId && ref.current) {
      ref.current.focus();
    }
  }, [focusedGroupId, props.groupId]);

  const getControls = () => {
    if (props.items.length < MAX_COLUMNS) {
      return {
        controls: 'ADD',
        onAdd: props.onAddItem,
        canAdd: props.hasItemsToAdd,
      } as const;
    } else {
      return {
        controls: 'NONE',
      } as const;
    }
  };

  /**
   * should technically not be a renderable state
   */
  if (props.items.length === 0) {
    return null;
  }

  const mappedStoryInventoryBlocks = props.items.map(
    mapStoryInventoryItemToBlockProps
  );

  return (
    <StoryContentBlock
      ref={ref}
      // TODO (storytelling): add support for focused state
      isFocused={focusedGroupId === props.groupId}
      onRemove={props.onRemoveGroup}
      {...getControls()}
    >
      <FrameGrid.Root maxColumns={props.items.length as 1 | 2}>
        {mappedStoryInventoryBlocks.map((node) => {
          return (
            <ContentBlockItemNft.Provider
              key={node.id}
              item={node}
              context={props.items.length === 1 ? 'SINGLE' : 'MULTI'}
              updateItem={(item) => {
                props.onUpdateItem(mapBlockPropsToStoryInventoryItem(item));
              }}
              removeItem={() => {
                props.onRemoveItem(node.id);
              }}
            >
              {match(node)
                .with({ state: 'POPULATED' }, (node) => {
                  const mappedNft = momentItemsById[node.inventoryItem.id];
                  return (
                    <ContentBlockItem.Root>
                      {props.items.length > 1 && <StoryInventoryItemControls />}
                      {/* should always be defined */}
                      {mappedNft && (
                        <StoryInventoryBlockContent inventoryItem={mappedNft} />
                      )}
                    </ContentBlockItem.Root>
                  );
                })
                .with({ state: 'EMPTY' }, () => {
                  return (
                    <>
                      <ContentBlockItem.Root>
                        <StoryInventoryItemControls />
                        <StoryNftBlockEmpty
                          momentId={props.momentId}
                          hasItemInGroup={props.items.length > 0}
                        />
                      </ContentBlockItem.Root>
                      <StoryNftModal
                        itemIdsInStory={props.itemIdsInStory}
                        onSelectItem={(nft) => {
                          props.onUpdateItem({
                            type: 'INVENTORY_ITEM',
                            id: node.id,
                            placeholder: null,
                            content: {
                              state: 'POPULATED',
                              inventoryItem: nft,
                            },
                          });
                        }}
                      />
                    </>
                  );
                })
                .exhaustive()}
            </ContentBlockItemNft.Provider>
          );
        })}
        <StoryToolbarPositioner>
          <ContentLayoutToolbar
            onLayoutChange={props.onLayoutChange}
            layout={props.layout}
          />
        </StoryToolbarPositioner>
      </FrameGrid.Root>
    </StoryContentBlock>
  );
}

export function StoryNftBlockEmpty(props: {
  momentId: number;
  hasItemInGroup: boolean;
}) {
  const modal = useModal();

  return (
    <StoryInventoryItemEmptyContainer>
      <StoryInventoryBlockEmptyContainer
        onClick={() => {
          modal.setModal({
            type: 'STORY_NFT',
            momentId: props.momentId,
          });
        }}
        style={{
          aspectRatio: props.hasItemInGroup ? '4 / 3' : '1 / 1',
        }}
      >
        <Button variant="ghost" icon size={0}>
          <PlusIcon />
          <span>Select work</span>
        </Button>
      </StoryInventoryBlockEmptyContainer>
      <FrameGridItemDetailsWrapper>
        <FrameGridItemDetails.Skeleton />
      </FrameGridItemDetailsWrapper>
    </StoryInventoryItemEmptyContainer>
  );
}

function StoryInventoryBlockContent(props: {
  inventoryItem: MomentInventoryItem;
}) {
  const contentItemContext = useContentItemNftContext();
  const item = contentItemContext.item;

  if (item.state === 'EMPTY') {
    return null;
  }

  return <StoryInventoryGridItem inventoryItem={props.inventoryItem} />;
}

function StoryInventoryGridItem(props: { inventoryItem: MomentInventoryItem }) {
  const { inventoryItem } = props;

  return match(inventoryItem)
    .with({ type: 'MARKET_NFT' }, (inventoryItem) => {
      const media = inventoryItem.media
        ? mapApiMediaToPreviewMediaAsset(inventoryItem.media)
        : null;

      const optimizedMedia = media
        ? NftGridItem.optimizeMedia(media, { maxColumns: 2 })
        : null;

      return <NftGridItem nft={inventoryItem} media={optimizedMedia} />;
    })
    .with({ type: 'SOLD_NFT' }, (inventoryItem) => {
      const media = inventoryItem.media
        ? mapApiMediaToPreviewMediaAsset(inventoryItem.media)
        : null;

      const optimizedMedia = media
        ? NftGridItem.optimizeMedia(media, { maxColumns: 2 })
        : null;

      return <NftGridItem.Sold nft={inventoryItem} media={optimizedMedia} />;
    })
    .with({ type: 'DROP_COLLECTION' }, (inventoryItem) => (
      <DropGridItem
        drop={inventoryItem}
        sale={mapDropSaleConfigurationToDropSale(
          inventoryItem.saleConfiguration
        )}
      />
    ))
    .with({ type: 'EDITION_COLLECTION' }, (inventoryItem) => (
      <EditionGridItem
        edition={inventoryItem}
        sale={inventoryItem.sale}
        version="legacy"
      />
    ))
    .exhaustive();
}

export function StoryInventoryBlock(props: { item: StoryInventoryItem }) {
  const mappedProps = mapStoryInventoryItemToBlockProps(props.item);

  if (mappedProps.state === 'EMPTY') {
    return null;
  }

  return (
    <StoryInventoryItemContainer>
      <StoryInventoryGridItem inventoryItem={mappedProps.inventoryItem} />
    </StoryInventoryItemContainer>
  );
}

function StoryInventoryItemControls() {
  const contentItemContext = useContentItemNftContext();

  if (contentItemContext.context === 'SINGLE') {
    return null;
  }

  return (
    <ContentBlockItem.Controls>
      <ControlButton
        variant="ghost"
        className={ContentBlockItem.BUTTON_CLASSNAME}
        icon="standalone"
        onClick={() => {
          contentItemContext.removeItem();
        }}
      >
        <TrashIcon />
      </ControlButton>
    </ContentBlockItem.Controls>
  );
}

const StoryInventoryItemContainer = styled('div', {
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: '$2',
});

const StoryImageBlockEmptyIconWrapper = styled('div', {
  height: '$avatar5',
  width: '$avatar5',
  backgroundColor: '$black5',
  borderRadius: '$round',
  padding: '$4',
  marginBottom: '$2',
  transition: 'background-color $1 $ease',
});

const StoryInventoryItemEmptyContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

const StoryInventoryBlockEmptyContainer = styled('div', {
  backgroundColor: '$black3',
  position: 'relative',

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  aspectRatio: '4 / 3',
  cursor: 'pointer',

  '&[role="button"]': {
    cursor: 'pointer',
  },

  '@hover': {
    '&:hover': {
      [`${StoryImageBlockEmptyIconWrapper}`]: {
        backgroundColor: '$black10',
      },
    },
  },
});

const FrameGridItemDetailsWrapper = styled('div', {
  marginTop: '$3',
  paddingX: '$1',
  maxWidth: '100%',

  '@bp0': {
    marginTop: '$4',
  },
});
